import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, BrandButton } from "@/components/PageHeader";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmtDate, showPin } from "@/lib/api";

const cedi = (n) => `GH₵ ${Number(n || 0).toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = () => new Date().toISOString().slice(0, 10);
const METHODS = ["Cash", "MoMo", "Card", "Bank", "Cheque"];
const th = "text-left font-semibold px-2 py-1.5 border-b border-gray-300 whitespace-nowrap";
const td = "px-2 py-1.5 border-b border-gray-100";

const OfficerTable = ({ officers, total, methods }) => (
  <table className="w-full text-xs" data-testid="cash-officers-table">
    <thead><tr><th className={th}>Officer</th>{METHODS.map((m) => <th key={m} className={`${th} text-right`}>{m}</th>)}<th className={`${th} text-right`}>Payments</th><th className={`${th} text-right`}>Total</th></tr></thead>
    <tbody>
      {officers.map((o) => (
        <tr key={o.id || o.name} data-testid={`cash-officer-${o.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
          <td className={td}>{o.name}</td>{METHODS.map((m) => <td key={m} className={`${td} text-right font-mono`}>{cedi(o.methods[m] || 0)}</td>)}
          <td className={`${td} text-right`}>{o.count}</td><td className={`${td} text-right font-mono font-semibold`}>{cedi(o.total)}</td>
        </tr>
      ))}
      {officers.length > 1 && <tr className="font-semibold" data-testid="cash-officers-total"><td className={td}>All officers</td>{methods.map((m) => <td key={m.method} className={`${td} text-right font-mono`}>{cedi(m.total)}</td>)}<td className={`${td} text-right`}>{officers.reduce((s, o) => s + o.count, 0)}</td><td className={`${td} text-right font-mono`}>{cedi(total)}</td></tr>}
    </tbody>
  </table>
);

const PaymentsTable = ({ rows }) => (
  <table className="w-full text-xs" data-testid="cash-payments-table">
    <thead><tr>{["Date & time", "Receipt No.", "Participant", "AHPC PIN", "Event", "Officer", "Method"].map((h) => <th key={h} className={th}>{h}</th>)}<th className={`${th} text-right`}>Amount</th></tr></thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.receipt_no}>
          <td className={`${td} whitespace-nowrap`}>{fmtDate(r.created_at)}</td><td className={`${td} font-mono`}>{r.receipt_no}</td><td className={td}>{r.delegate_name}</td>
          <td className={`${td} font-mono`}>{showPin(r.pin)}</td><td className={td}>{r.event_title}</td><td className={td}>{r.recorded_by}</td><td className={td}>{r.type === "refund" ? `Refund · ${r.method}` : r.method}</td>
          <td className={`${td} text-right font-mono`}>{cedi(r.amount)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const SpecialCard = ({ label, v, testId }) => (
  <div className="rounded-md border p-3" data-testid={testId}><div className="text-[11px] text-gray-500">{label} · {v.count}</div><div className="font-mono font-semibold">{cedi(v.total)}</div></div>
);

const SpecialSummary = ({ s }) => (
  <section className="mt-8 break-inside-avoid" data-testid="cash-special-summary">
    <h3 className="font-semibold text-sm">Special receipts</h3>
    <p className="text-[11px] text-gray-500 mb-2">Dues, donations and other special payments. Shown separately; not included in the net collected above.</p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <SpecialCard label="Received (paid)" v={s.paid} testId="cash-special-paid" />
      <SpecialCard label="Pending" v={s.pending} testId="cash-special-pending" />
      <SpecialCard label="Voided" v={s.voided} testId="cash-special-voided" />
    </div>
    {s.methods.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-testid="cash-special-methods">{s.methods.map((m) => <span key={m.method} className="text-xs rounded-full border px-3 py-1">{m.method} · {m.count} · <span className="font-mono font-semibold">{cedi(m.total)}</span></span>)}</div>}
  </section>
);

const Report = ({ data, eventName }) => (
  <div className="print-report bg-white border border-[#E5E7EB] rounded-lg p-6 text-[#111]" data-testid="cash-report-document">
    <div className="flex items-center gap-3 border-b-4 border-[#E60000] pb-4">
      <img src="/images/gamls-logo.png" alt="GAMLS" className="w-12 h-12" />
      <div className="flex-1">
        <div className="font-heading font-extrabold">Ghana Association of Medical Laboratory Scientists</div>
        <div className="text-xs text-gray-500">Cash Report · {data.date_from === data.date_to ? data.date_from : `${data.date_from} to ${data.date_to}`} · {eventName}{data.scope === "own" ? ` · ${data.generated_by} only` : ""}</div>
      </div>
      <div className="text-right"><div className="text-xs text-gray-500">Net collected</div><div className="font-heading text-2xl font-extrabold" data-testid="cash-report-total">{cedi(data.total)}</div></div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 my-5" data-testid="cash-methods">
      {data.methods.map((m) => <div key={m.method} className="rounded-md border p-3" data-testid={`cash-method-${m.method.toLowerCase()}`}><div className="text-[11px] text-gray-500">{m.method} · {m.count}</div><div className="font-mono font-semibold">{cedi(m.total)}</div></div>)}
    </div>
    <h3 className="font-semibold text-sm mb-2">By officer</h3>
    {data.officers.length ? <OfficerTable officers={data.officers} total={data.total} methods={data.methods} /> : <p className="text-sm text-gray-500" data-testid="cash-report-empty">No payments recorded in this period.</p>}
    {data.payments.length > 0 && <><h3 className="font-semibold text-sm mt-6 mb-2">All payments ({data.count})</h3><PaymentsTable rows={data.payments} /></>}
    {data.special && <SpecialSummary s={data.special} />}
    <div className="grid grid-cols-2 gap-10 mt-10 text-xs text-gray-600">
      <div className="border-t pt-2">Prepared by: {data.generated_by} · {fmtDate(data.generated_at)}</div>
      <div className="border-t pt-2">Checked by (name & signature)</div>
    </div>
  </div>
);

export default function CashReport() {
  const { events } = useApp();
  const [range, setRange] = useState({ from: today(), to: today() });
  const [eventId, setEventId] = useState("all");
  const [data, setData] = useState(null);
  const load = () => api.get("/reports/cash", { params: { date_from: range.from, date_to: range.to, event_id: eventId === "all" ? "" : eventId } })
    .then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, eventId]);
  const eventName = eventId === "all" ? "All events" : events.find((e) => e.id === eventId)?.title || "Event";
  return (
    <div data-testid="cash-report-page">
      <PageHeader eyebrow="Accounts" title="Daily Cash Report" description="Payments taken in person, totalled by Accounts Officer and payment method. Pick a date range, then print for end-of-day handover."
        actions={<BrandButton variant="dark" onClick={() => window.print()} disabled={!data} data-testid="cash-report-print"><Printer className="w-4 h-4" />Print report</BrandButton>} />
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4 items-stretch sm:items-end">
        <label className="text-xs font-semibold text-gray-600">From<Input type="date" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))} className="h-10 mt-1 bg-white" data-testid="cash-date-from" /></label>
        <label className="text-xs font-semibold text-gray-600">To<Input type="date" value={range.to} min={range.from} onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))} className="h-10 mt-1 bg-white" data-testid="cash-date-to" /></label>
        <Select value={eventId} onValueChange={setEventId}><SelectTrigger className="sm:w-64 h-10 bg-white" data-testid="cash-event-filter"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All events</SelectItem>{events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent></Select>
        <BrandButton variant="outline" onClick={load} data-testid="cash-report-refresh"><RefreshCw className="w-4 h-4" />Refresh</BrandButton>
      </div>
      <div className="overflow-x-auto">{data ? <Report data={data} eventName={eventName} /> : <div className="text-gray-500">Loading…</div>}</div>
    </div>
  );
}
